// src/components/Header/NotificationMenu.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Badge, Box, Button, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MarkAsUnreadIcon from '@mui/icons-material/MarkAsUnread';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { addOrEditNotification, getNotifications, setNotification } from '../../redux/slices/notificationSlice';
import { setPopupNotification } from '../../redux/slices/popupNotificationSlice';
import { setPopupBasic } from '../../redux/slices/popupBasicSlice';
import TooltipBasic from '../Controls/Inputs/TooltipBasic';
import PopupBasic from '../Controls/Popups/PopupBasic';
import PopupRegular from '../Controls/Popups/PopupRegular';
import Notifications from '../Apm/Notification/Notifications';
import { notificationReadURL } from '../Data/baseURL';
import axiosInstance from '../../services/axios/axios';

//----------------------------------- CONSTANTS -----------------------------------//

const MENU_ITEM_HEIGHT = 50;
const MAX_VISIBLE_MENU_ITEMS = 9.5;

//----------------------------------- COMPONENT -----------------------------------//

const NotificationMenu = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { notifications = [], status = null } = useSelector((state) => state.notificationSlice ?? {});

  const [anchorEl, setAnchorEl] = useState(null);

  const [markingReadId, setMarkingReadId] = useState(null);

  const isMenuOpen = Boolean(anchorEl);

  useEffect(() => {
    if (status === null) {
      dispatch(getNotifications());
    }
  }, [dispatch, status]);

  const unreadNotifications = useMemo(
    () => notifications.filter((notification) => notification?.read === false),
    [notifications]
  );

  const unreadCount = unreadNotifications.length;

  const handleMenuOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleNotificationCenterOpen = useCallback(() => {
    dispatch(setPopupNotification(true));

    handleMenuClose();
  }, [dispatch, handleMenuClose]);

  const handleNotificationOpen = useCallback(
    (notification) => {
      batch(() => {
        dispatch(setNotification(notification));

        dispatch(setPopupBasic(true));
      });

      handleMenuClose();
    },
    [dispatch, handleMenuClose]
  );

  const handleMarkAsRead = useCallback(
    async (event, notification) => {
      event.stopPropagation();

      if (!notification?.id || markingReadId !== null) {
        return;
      }

      setMarkingReadId(notification.id);

      try {
        const response = await axiosInstance.patch(`${notificationReadURL}${notification.id}/`, {
          pk: notification.id,
        });

        /*
         * Some endpoints return the updated object.
         * Others may return an empty 204 response.
         */
        const updatedNotification =
          response.data && typeof response.data === 'object'
            ? response.data
            : {
                ...notification,
                read: true,
              };

        dispatch(addOrEditNotification(updatedNotification));
      } catch (error) {
        console.error('Notification update failed:', error);

        toast.error(() => t('toast.not_updated_try_again'));
      } finally {
        setMarkingReadId(null);
      }
    },
    [dispatch, markingReadId, t]
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <TooltipBasic title={t('notification.tooltip')} placement="bottom-start">
          <IconButton
            id="notification-button"
            aria-label={t('notification.tooltip')}
            aria-controls={isMenuOpen ? 'notification-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? 'true' : undefined}
            color="inherit"
            sx={{
              mt: 0.5,
              ml: 2,
            }}
            onClick={handleMenuOpen}
          >
            <Badge badgeContent={unreadCount} color="secondary" max={99}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </TooltipBasic>
      </Box>

      <Menu
        id="notification-menu"
        aria-labelledby="notification-button"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        PaperProps={{
          elevation: 2,

          sx: {
            mt: 2,

            width: {
              xs: 'calc(100vw - 24px)',
              sm: 500,
            },

            maxWidth: 600,

            maxHeight: MENU_ITEM_HEIGHT * MAX_VISIBLE_MENU_ITEMS,

            filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.22))',
          },
        }}
      >
        <MenuItem
          divider
          onClick={handleNotificationCenterOpen}
          sx={{
            justifyContent: 'center',
            py: 1.25,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" fontWeight={500}>
              {t('notification.center')}
            </Typography>

            <OpenInNewIcon color="primary" fontSize="small" />
          </Stack>
        </MenuItem>

        {unreadCount === 0 ? (
          <MenuItem
            disabled
            sx={{
              py: 2,
              whiteSpace: 'normal',
            }}
          >
            <Typography color="text.secondary">{t('notification.no_new_notifications')}</Typography>
          </MenuItem>
        ) : (
          unreadNotifications.map((notification, index) => {
            const isMarkingRead = markingReadId === notification.id;

            return (
              <MenuItem
                key={notification.id}
                divider={index < unreadCount - 1}
                onClick={() => handleNotificationOpen(notification)}
                sx={{
                  gap: 1.5,
                  py: 1.25,
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography noWrap fontWeight={500}>
                    {notification.title}
                  </Typography>

                  <Typography noWrap color="text.secondary" fontWeight={300}>
                    {notification.message}
                  </Typography>
                </Box>

                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  color="warning"
                  disabled={isMarkingRead}
                  endIcon={<MarkAsUnreadIcon />}
                  onClick={(event) => handleMarkAsRead(event, notification)}
                  sx={{
                    display: {
                      xs: 'none',
                      sm: 'inline-flex',
                    },

                    flexShrink: 0,
                  }}
                >
                  {t('button.mark_as_read')}
                </Button>

                <IconButton
                  type="button"
                  color="warning"
                  disabled={isMarkingRead}
                  aria-label={t('button.mark_as_read')}
                  onClick={(event) => handleMarkAsRead(event, notification)}
                  sx={{
                    display: {
                      xs: 'inline-flex',
                      sm: 'none',
                    },

                    flexShrink: 0,
                  }}
                >
                  <MarkAsUnreadIcon />
                </IconButton>
              </MenuItem>
            );
          })
        )}
      </Menu>

      <PopupRegular title={t('notification.center')}>
        <Notifications />
      </PopupRegular>

      <PopupBasic />
    </>
  );
};

export default NotificationMenu;
